<template>
    <Dialog v-model:visible="display" :modal="true" class="reg-dialog finance" :baseZIndex="104" :closable="false">
        <template #header>
            <Header />
        </template>
        <div class="box">
            <div class="box-content">
                <div class="main">
                    <img class="dots-top" src="/kandidaten/img/dot-pattern-top.svg" v-if="!showSpinner" />
                    <img class="dots-bottom" src="/kandidaten/img/dot-pattern-bottom.svg" v-if="!showSpinner" />
                    <div class="progress-wrapper" v-if="showSpinner">
                        <ProgressSpinner />
                    </div>
                    <div class="content" v-else>
                        <h1>
                            {{ t("registration.headline2") }}
                            {{ t("registration.headline3") }}
                        </h1>
                        <div class="intro">{{ t("registration.headline4") }}</div>
                        <div class="reg-form">
                            <div>
                                <span class="p-float-label">
                                    <InputText id="username" type="username" v-model="user" autofocus :onkeypress="checkMail()"
                                               data-cy="reg-usermail"
                                               :class="{ 'p-invalid': showLoginInsteadOfContinue }" />
                                    <label for="username">{{ t("registration.label1") }} *</label>
                                </span>

                                <small id="no-email" class="p-error" v-if="submit && !validEmail">
                                    {{ t("registration.error1") }}
                                </small>
                                <small id="no-email" class="p-error" v-if="showLoginInsteadOfContinue">
                                    {{ t("registration.error3") }}
                                </small>
                            </div>
                            <div>
                                <span class="p-float-label">
                                    <Password id="new-password" v-model="password" autocomplete="one-time-code"
                                              :feedback="false"
                                              toggleMask
                                              data-cy="reg-password">
                                        <template #header>
                                            <h3>{{ t("registration.password.choose") }}</h3>
                                            <h6>{{ t("registration.password.strength") }}</h6>
                                        </template>
                                        <template #footer="sp">
                                            {{ sp.level }}
                                            <Divider />
                                            <h6>{{ t("registration.password.hint") }}</h6>
                                            <ul class="p-pl-2 p-ml-2 p-mt-0" style="line-height: 1.5">
                                                <li>{{ t("registration.password.hint.signs") }}</li>
                                                <!--<li>Mindestens ein Kleinbuchstabe</li>
                                                <li>Mindestens ein Großbuchstabe</li>
                                                <li>Mindestens eine Zahl</li>
                                                <li>Mindestens ein Sonderzeichen</li>-->
                                            </ul>
                                        </template>
                                    </Password>
                                    <label for="password">{{ t("password") }} *</label>
                                </span>
                                <small class="p-error" v-if="submit && !password">
                                    {{ t("registration.password.missing") }}
                                </small>
                                <small id="password-weak" class="p-error" v-if="password && password.length < 8">
                                    {{ t("registration.password.safety") }} <span> {{(8 - password.length)}} {{t('registration.password.charleft')}}</span>
                                </small>
                            </div>
                            <div>
                                <span class="p-float-label">
                                    <Password id="new-password2"
                                              v-model="password2"
                                              :feedback="false"
                                              toggleMask
                                              autocomplete="one-time-code"
                                              data-cy="reg-password-repeat" />
                                    <label for="password2">{{ t("registration.password.repeat") }} *</label>
                                </span>
                                <small class="p-error" v-if="submit && !password2">
                                    {{ t("registration.password.missing") }}
                                </small>
                                <small id="password-missmatch" class="p-error" v-if="submit && password != password2 && password && password2">
                                    {{ t("registration.password.notthesame") }}
                                </small>
                            </div>
                            <div>
                                <span class="p-float-label terms-and-conditions p-mb-4">
                                    <Checkbox id="termsandconditions" data-cy="termsAndConditionsCheckbox"
                                              v-model="acceptTermsAndConditions" :binary="true" />
                                    <label class="pointerevents no-tooltip"
                                           for="termsandconditions">
                                        {{ t("registration.data.policy.label1") }} <a href="/datenschutz"
                                                                                      target="_blank">{{ t("privacy.policy") }}</a>{{ t("registration.data.policy.label2") }}
                                    </label>
                                </span>
                                <small class="p-error" v-if="submit && !acceptTermsAndConditions">
                                    {{ t("registration.data.policy.please.accept") }}
                                </small>
                            </div>
                            <b class="p-error" v-if="error">{{ error }}</b>
                            <div class="mobile-bottom-nav p-mt-3">
                                <Button v-if="!showLoginInsteadOfContinue" :label="t('registration.headline6')" @click="signup"
                                        data-cy="button-profile-next"
                                        class="button-primary big stretch" />

                                <Button v-else :label="t('registration.button.continue.login')" @click="login"
                                        data-cy="button-profile-next"
                                        class="button-primary big stretch" />
                            </div>
                            <div class='free'><i class="pi pi-lock"></i> {{ t("registration.headline5") }}</div>
                        </div>
                    </div>
                </div>
                <div class="sidebar">
                    <div class="content">
                        <div class="mobile-hide sidebar-image"><img src="/kandidaten/img/Step 0.png" /></div>
                        <div class="mobile-hide headline">{{ t("registration.headline1") }}</div>
                    </div>
                </div>
            </div>
        </div>

    </Dialog>
</template>

<script lang="ts" setup>
    import { computed, defineEmits, defineExpose, getCurrentInstance, ref } from "vue";
    import { useI18n } from "vue-i18n";
    import { createAuthenticationService } from '../../services/authenticationService';
    import { useRoute } from "vue-router";
    import { BrandId } from "@/models/BrandIds";
    import validator from "validator";
    import Header from "@/components/Header.vue";

    const emit = defineEmits(['closed', 'registered', 'login']);
    const { t } = useI18n();

    const app = getCurrentInstance();
    const display = ref(false);
    const route = useRoute();
    const user = ref();
    const password = ref();
    const password2 = ref();
    const showLoginInsteadOfContinue = ref(false);
    const close = function () {
        display.value = false;
        emit("closed");
    }

    const show = function (email) {
        user.value = email;
        showSpinner.value = false;
        error.value = undefined;
        display.value = true;
    }
    const submit = ref(false);
    const error = ref();
    const showSpinner = ref(false);
    const acceptTermsAndConditions = ref(false);

    const authenticationService = createAuthenticationService(app);

    const checkMail = async () => {

        if (!user.value) {
            showLoginInsteadOfContinue.value = false;
            return;
        }

        if (!validator.isEmail(user.value)) {
            showLoginInsteadOfContinue.value = false;
            return;
        }
        showLoginInsteadOfContinue.value = await authenticationService.emailAlreadyExists(user.value);
    }

    const validEmail = computed(() => {
        if (!user.value) return false;
        let isValidEmail = validator.isEmail(user.value);
        if (isValidEmail) {
            for (var i = 0; i < window.invalidDomains.length; i++) {
                if (user.value.toLowerCase().includes(window.invalidDomains[i].toLowerCase())) {
                    return false;
                }
            }
            return true;
        } else {
            return isValidEmail;
        }
    });

    const login = () => {
        emit("login", null, { email: user.value });
        display.value = false;
    }

    const signup = async () => {

        submit.value = true;

        if (!password.value || password.value.length < 8 || !validEmail.value)
            return;

        if (password2.value != password.value)
            return;

        if (!acceptTermsAndConditions.value)
            return;

        showSpinner.value = true;
        error.value = undefined;

        const singUpSuccess = await authenticationService.signUpWithPassword({
            brandId: BrandId.BigFour,
            emailAddress: user.value,
            password: password.value,
            encryptedCandidateId: null,
            source: route.query.source ? route.query.source?.toString() : "organic"
        });

        if (singUpSuccess) {
            emit("registered");
            close();
        } else {
            if (singUpSuccess == false) {
                error.value = t("registration.error2");
            }
            if (singUpSuccess == null) {
                error.value = t("registration.error3");
            }
        }
        showSpinner.value = false;
    }

    defineExpose({
        show,
        close,
    });

</script>

<style>
    .p-input-icon-right > i:last-of-type {
        color: blue !important;
    }
</style>
